import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { OrbitControls } from '@react-three/drei';
import { useThree, useFrame } from '@react-three/fiber';
import { Ocean } from '@/world/Ocean';
import { Sky } from '@/world/Sky';
import { Atmosphere } from '@/world/Atmosphere';
import { TerrainSphere, getIslandData, RADIUS } from '@/world/TerrainSphere';
import { useSunCycle } from '@/hooks/useSunCycle';
import { FirstPersonCamera } from '@/r3f/FirstPersonCamera';
import { ShipController } from '@/ships/ShipController';
import { HorseController } from '@/mobs/HorseController';
import { HorseMountDetector } from '@/mobs/HorseMountDetector';
import { Clouds } from '@/world/Clouds';
import { SunLighting } from '@/world/SunLighting';
import { GrassRenderer } from '@/world/GrassRenderer';
import { VillageRenderer } from '@/world/villages';
import { MobRenderer, MobType } from '@/mobs';
import { DemonRenderer } from '@/mobs/demons';
import { InteractionDetector, getDockPlacements } from '@/interaction';
import { useUIStore } from '@/state/useUIStore';
import { useShipStore } from '@/state/useShipStore';
import { useMountStore } from '@/state/useMountStore';
import { getAllMobs } from '@/mobs/mobRegistry';
import { playerNormal } from '@/state/playerPosition';
import { DockedShips } from '@/ships/DockedShips';

// Terrain peaks can reach ~radius * 1.10, so orbit camera starts well above
const ORBIT_MIN_DISTANCE = RADIUS * 1.50;
const ORBIT_MAX_DISTANCE = RADIUS * 3;

/** Sets camera to a proper orbit distance on mount so we don't start inside terrain */
function OrbitCameraSetup() {
  const { camera } = useThree();
  const initialized = useRef(false);
  if (!initialized.current) {
    initialized.current = true;
    const phi = 1.0;
    const theta = 0.4;
    const d = ORBIT_MIN_DISTANCE * 1.1;
    camera.position.set(
      Math.sin(phi) * Math.sin(theta) * d,
      Math.cos(phi) * d,
      Math.sin(phi) * Math.cos(theta) * d,
    );
    camera.lookAt(0, 0, 0);
  }
  return null;
}

// Progress ranges for non-terrain stages (terrain owns 15–45% internally)
const POST_TERRAIN_STAGES = [
  { pct: 45, label: 'Building ocean...' },
  { pct: 60, label: 'Placing villages...' },
  { pct: 80, label: 'Forming clouds...' },
  { pct: 95, label: 'Finalizing...' },
] as const;

/**
 * Waits for a few rendered frames after all stages are done so the GPU
 * finishes shader compilation and at least one full frame is on screen.
 */
function WaitForGPU({ onReady }: { onReady: () => void }) {
  const fired = useRef(false);
  const frameCount = useRef(0);
  useFrame(() => {
    if (fired.current) return;
    frameCount.current++;
    if (frameCount.current >= 10) {
      fired.current = true;
      onReady();
    }
  });
  return null;
}

// Lobby scene - decorative background with ocean and sky
export function LobbyScene({ onReady }: { onReady?: () => void } = {}) {
  const sun = useSunCycle(300);
  const islands = useMemo(() => getIslandData(RADIUS), []);
  const [firstPerson, setFirstPerson] = useState(false);
  const aboard = useShipStore((s) => s.aboard);
  const [spawnPos, setSpawnPos] = useState<{ theta: number; phi: number; key: number } | null>(null);

  // Stage 0 = sky/lighting, stage 1 = terrain (async), stages 2–5 = ocean/villages/clouds/done
  const [stage, setStage] = useState(0);

  const setLoadProgress = useUIStore((s) => s.setLoadProgress);

  // Stage 0 → 1: immediate advance (sky is lightweight)
  useEffect(() => {
    if (stage !== 0) return;
    setLoadProgress(0, 'Preparing scene...');
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setStage(1));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [stage, setLoadProgress]);

  // Stage 1 → 2: terrain signals completion via onReady callback
  const handleTerrainReady = useCallback(() => {
    setStage(2);
  }, []);

  // Stages 2–5: advance via double-rAF (ocean, villages, clouds, done)
  useEffect(() => {
    if (stage < 2 || stage > 5) return;

    const postIdx = stage - 2; // 0..3
    if (postIdx < POST_TERRAIN_STAGES.length) {
      const { pct, label } = POST_TERRAIN_STAGES[postIdx];
      setLoadProgress(pct, label);
    }

    // Stage 5 = all mounted, wait for GPU
    if (stage >= 5) return;

    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setStage((s) => s + 1));
    });
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2); };
  }, [stage, setLoadProgress]);

  const mounted = useMountStore((s) => s.mounted);

  // V key toggles first/third person, H key mounts/dismounts horse
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key === 'v' && !useShipStore.getState().aboard && !useMountStore.getState().mounted) {
        if (document.pointerLockElement) document.exitPointerLock();
        setFirstPerson((prev) => !prev);
      }
      if (key === 'h' && firstPerson && !useShipStore.getState().aboard && !useMountStore.getState().mounted) {
        // Find nearest horse within ~40 units
        const allMobs = getAllMobs();
        const pn = playerNormal;
        let nearest = null as typeof allMobs[0] | null;
        let nearestDot = -1;
        for (const mob of allMobs) {
          if (mob.type !== MobType.Horse || mob.mounted) continue;
          const dot = mob.nx * pn.x + mob.ny * pn.y + mob.nz * pn.z;
          if (dot > 0.9999 && dot > nearestDot) {  // ~0.004 rad ≈ 40 units
            nearestDot = dot;
            nearest = mob;
          }
        }
        if (nearest) {
          if (document.pointerLockElement) document.exitPointerLock();
          useMountStore.getState().mount(nearest);
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [firstPerson]);

  // Dismount horse: return to first-person walking at horse position
  const handleDismount = useCallback((theta: number, phi: number) => {
    useMountStore.getState().dismount();
    setSpawnPos({ theta, phi, key: Date.now() });
    setFirstPerson(true);
    if (document.pointerLockElement) document.exitPointerLock();
  }, []);

  // Disembark handler: docks the ship and spawns player near the dock
  const handleDisembark = useCallback((_theta: number, _phi: number, dockIndex: number) => {
    useShipStore.getState().disembark(dockIndex);

    // Compute dock spherical coords for player spawn
    const docks = getDockPlacements();
    if (dockIndex >= 0 && dockIndex < docks.length) {
      const pos = docks[dockIndex].position;
      const len = pos.length();
      if (len > 0) {
        const nx = pos.x / len, ny = pos.y / len, nz = pos.z / len;
        const phi = Math.acos(Math.max(-1, Math.min(1, ny)));
        const theta = Math.atan2(nx, nz);
        setSpawnPos({ theta, phi, key: Date.now() });
      }
    }

    setFirstPerson(true);
    if (document.pointerLockElement) document.exitPointerLock();
  }, []);

  return (
    <>
      {/* Camera mode: ship > mounted horse > first person > orbit */}
      {aboard ? (
        <ShipController
          sphereRadius={RADIUS}
          onDisembark={handleDisembark}
          sunPosition={sun}
        />
      ) : mounted ? (
        <HorseController
          sphereRadius={RADIUS}
          onDismount={handleDismount}
        />
      ) : firstPerson ? (
        <FirstPersonCamera
          key={spawnPos?.key ?? 0}
          sphereRadius={RADIUS}
          {...(spawnPos && { startTheta: spawnPos.theta, startPhi: spawnPos.phi })}
        />
      ) : (
        <>
          <OrbitCameraSetup />
          <OrbitControls
            enableDamping
            dampingFactor={0.05}
            minDistance={ORBIT_MIN_DISTANCE}
            maxDistance={ORBIT_MAX_DISTANCE}
          />
        </>
      )}

      {/* Stage 0: Lighting + Sky (lightweight) */}
      <SunLighting sunPosition={sun} />
      <Sky sunPosition={sun} radius={RADIUS} />

      {/* Stage 1: Terrain (async — reports its own sub-progress 15–45%) */}
      {stage >= 1 && (
        <TerrainSphere sunPosition={sun} onReady={handleTerrainReady} />
      )}

      {/* Stage 2: Ocean + Grass */}
      {stage >= 2 && (
        <>
          <Ocean radius={RADIUS} resolution={300} islands={islands} sunPosition={sun} />
          <GrassRenderer sunPosition={sun} />
        </>
      )}

      {/* Stage 3: Villages + Mobs + Interaction + Docked ships */}
      {stage >= 3 && (
        <>
          <VillageRenderer maxVillages={30} sunPosition={sun} />
          <MobRenderer maxVillages={30} />
          <DemonRenderer count={100} />
          {!aboard && !mounted && <InteractionDetector />}
          {!aboard && !mounted && firstPerson && <HorseMountDetector />}
          <DockedShips sphereRadius={RADIUS} sunPosition={sun} />
        </>
      )}

      {/* Stage 4: Clouds + Atmosphere */}
      {stage >= 4 && (
        <>
          <Atmosphere radius={RADIUS} sunPosition={sun} />
          <Clouds radius={RADIUS} sunPosition={sun} />
        </>
      )}

      {/* Stage 5+: Wait for GPU shader compilation */}
      {stage >= 5 && onReady && (
        <WaitForGPU onReady={() => {
          setLoadProgress(100, 'Ready');
          onReady();
        }} />
      )}
    </>
  );
}
